import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  Textarea,
  useToast,
  useColorModeValue,
  IconButton,
  Progress,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Image,
  Center,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Grid,
  Spinner,
  useBreakpointValue
} from '@chakra-ui/react';
import { FiArrowLeft, FiX, FiPlus, FiEdit, FiTrash2 } from 'react-icons/fi';
import IconWrapper from '../components/IconWrapper';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { GENRES } from '../constants/genres';
import { CONTENT_RATINGS } from '../constants/contentRatings';
import { uploadFileDirect } from '../services/uploadService';
import { updateContent, getContentForEdit } from '../services/contentUpdateService';
import { deleteContent } from '../services/contentDeletionService';

interface VolumeData {
  id: string;
  title: string;
  coverFile: File | null;
  coverUrl: string;
  pages?: number;
  description: string;
  file?: File | null;
  fileName?: string;
  existingCoverUrl?: string;
  existingFileUrl?: string;
}

interface ComicData {
  id: string;
  title: string;
  description: string;
  category: string;
  visibility: string;
  genre: string;
  genres: string[];
  contentRating: string;
  language: string;
  languages: string[];
  year: number;
  duration: number;
  author: string;
  cast: string;
  director: string;
  writer: string;
  tags: string;
  assets?: {
    coverFile?: string;
    volumes?: Array<{
      title?: string;
      description?: string;
      volumeNumber?: number;
      pages?: number;
      coverUrl?: string;
      fileUrl?: string;
    }>;
  };
}

const EditComic: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [comic, setComic] = useState<ComicData | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'comic',
    visibility: 'public',
    genre: '',
    genres: [] as string[],
    contentRating: 'G',
    language: 'English',
    languages: [] as string[],
    year: new Date().getFullYear(),
    duration: 0,
    author: '',
    cast: '',
    director: '',
    writer: '',
    tags: '',
    coverFile: null as File | null,
    coverUrl: '',
    volumes: [] as VolumeData[]
  });

  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'white');
  const labelColor = useColorModeValue('gray.600', 'gray.400');

  useEffect(() => {
    if (id) {
      loadComicData();
    }
  }, [id]);

  const loadComicData = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const comicData = await getContentForEdit(id, user?.uid || '');
      
      if (comicData) {
        setComic(comicData as unknown as ComicData);
        
        // Convert existing volumes to VolumeData format
        const existingVolumes: VolumeData[] = (comicData.assets?.volumes || []).map((vol: any, index: number) => ({
          id: `volume-${Date.now()}-${index}`,
          title: vol.title || '',
          coverFile: null,
          coverUrl: vol.coverUrl || '',
          pages: vol.pages,
          description: vol.description || '',
          file: null,
          fileName: vol.fileUrl ? `Volume ${vol.volumeNumber || index + 1}.pdf` : '',
          existingCoverUrl: vol.coverUrl,
          existingFileUrl: vol.fileUrl
        }));

        setFormData({
          title: comicData.title || '',
          description: comicData.description || '',
          category: comicData.category || 'comic',
          visibility: comicData.visibility || 'public',
          genre: comicData.genre || '',
          genres: comicData.genres || [],
          contentRating: comicData.contentRating || 'G',
          language: comicData.language || 'English',
          languages: comicData.languages || [],
          year: comicData.year || new Date().getFullYear(),
          duration: comicData.duration || 0,
          author: (comicData as any).author || '',
          cast: (comicData as any).cast || '',
          director: (comicData as any).director || '',
          writer: (comicData as any).writer || '',
          tags: (comicData as any).tags || '',
          coverFile: null,
          coverUrl: comicData.assets?.coverFile || comicData.thumbnail || '',
          volumes: existingVolumes
        });
      }
    } catch (error) {
      console.error('Error loading comic data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load comic data.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGenreToggle = (genre: string) => {
    setFormData(prev => ({
      ...prev,
      genres: prev.genres.includes(genre)
        ? prev.genres.filter(g => g !== genre)
        : [...prev.genres, genre].slice(0, 3)
    }));
  };

  const handleLanguageToggle = (language: string) => {
    setFormData(prev => ({
      ...prev,
      languages: prev.languages.includes(language)
        ? prev.languages.filter(l => l !== language)
        : [...prev.languages, language]
    }));
  };

  const handleCoverSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setFormData(prev => ({
        ...prev,
        coverFile: file,
        coverUrl: url
      }));
    }
  };

  const handleCoverRemove = () => {
    setFormData(prev => ({
      ...prev,
      coverFile: null,
      coverUrl: prev.coverUrl.includes('blob:') ? '' : prev.coverUrl
    }));
  };

  const addVolume = () => {
    const newVolume: VolumeData = {
      id: `volume-${Date.now()}`,
      title: '',
      coverFile: null,
      coverUrl: '',
      pages: undefined,
      description: '',
      file: null,
      fileName: ''
    };
    handleInputChange('volumes', [...formData.volumes, newVolume]);
  };

  const updateVolume = (volumeId: string, field: keyof VolumeData, value: any) => {
    const updatedVolumes = formData.volumes.map(vol => 
      vol.id === volumeId ? { ...vol, [field]: value } : vol
    );
    handleInputChange('volumes', updatedVolumes);
  };

  const updateVolumeFile = (volumeId: string, file: File) => {
    const updatedVolumes = formData.volumes.map(vol => 
      vol.id === volumeId ? { ...vol, file: file, fileName: file.name } : vol
    );
    handleInputChange('volumes', updatedVolumes);
  };

  const removeVolume = (volumeId: string) => {
    const updatedVolumes = formData.volumes.filter(vol => vol.id !== volumeId);
    handleInputChange('volumes', updatedVolumes);
  };

  const handleVolumeCoverSelect = (volumeId: string, file: File) => {
    const url = URL.createObjectURL(file);
    const updatedVolumes = formData.volumes.map(vol => 
      vol.id === volumeId ? { ...vol, coverFile: file, coverUrl: url } : vol
    );
    handleInputChange('volumes', updatedVolumes);
  };

  const handleVolumeCoverRemove = (volumeId: string) => {
    const updatedVolumes = formData.volumes.map(vol => 
      vol.id === volumeId ? { 
        ...vol, 
        coverFile: null, 
        coverUrl: vol.coverUrl.includes('blob:') ? (vol.existingCoverUrl || '') : vol.coverUrl
      } : vol
    );
    handleInputChange('volumes', updatedVolumes);
  };

  const handleVolumeFileRemove = (volumeId: string) => {
    const updatedVolumes = formData.volumes.map(vol => 
      vol.id === volumeId ? { 
        ...vol, 
        file: null, 
        fileName: vol.fileName?.includes('.pdf') ? '' : vol.fileName
      } : vol
    );
    handleInputChange('volumes', updatedVolumes);
  };

  const handleSave = async () => {
    if (!user || !id) return;

    if (formData.volumes.length === 0) {
      toast({
        title: 'Volumes required',
        description: 'Please add at least one volume to your comic.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSaving(true);

    try {
      // Prepare update data
      const updateData: any = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        visibility: formData.visibility,
        genre: formData.genre,
        genres: formData.genres,
        contentRating: formData.contentRating,
        language: formData.language,
        languages: formData.languages,
        year: formData.year,
        duration: formData.duration,
        author: formData.author,
        cast: formData.cast,
        director: formData.director,
        writer: formData.writer,
        tags: formData.tags,
        coverFile: formData.coverFile || undefined
      };

      // Upload new cover if selected
      if (formData.coverFile) {
        const coverUrl = await uploadFileDirect(`comics/${user.uid}/covers`, formData.coverFile);
        updateData.coverFile = coverUrl;
      }

      // Process volumes
      const volumesMeta: Array<{
        title: string;
        description: string;
        volumeNumber: number;
        pages?: number;
        coverUrl?: string;
        fileUrl?: string;
      }> = [];

      for (let i = 0; i < formData.volumes.length; i++) {
        const volume = formData.volumes[i];
        
        let volumeCoverUrl = volume.existingCoverUrl || '';
        if (volume.coverFile) {
          volumeCoverUrl = await uploadFileDirect(`comics/${user.uid}/${id}/volumes/${i + 1}/covers`, volume.coverFile);
        }

        let volumeFileUrl = volume.existingFileUrl || '';
        if (volume.file) {
          volumeFileUrl = await uploadFileDirect(`comics/${user.uid}/${id}/volumes/${i + 1}`, volume.file);
        }

        volumesMeta.push({
          title: volume.title,
          description: volume.description,
          volumeNumber: i + 1,
          pages: volume.pages || undefined,
          coverUrl: volumeCoverUrl || undefined,
          fileUrl: volumeFileUrl || undefined,
        });
      }

      // Update the comic document
      await updateContent(id, user.uid, updateData);
      
      // Update volumes metadata
      if (volumesMeta.length > 0) {
        const { doc, updateDoc } = await import('firebase/firestore');
        const { db } = await import('../lib/firebase');
        await updateDoc(doc(db, 'content', id), {
          'assets.volumes': volumesMeta
        });
      }

      toast({
        title: 'Comic updated successfully!',
        description: 'Your comic has been updated.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      navigate(`/comic/${id}`);
    } catch (error) {
      console.error('Error updating comic:', error);
      toast({
        title: 'Update failed',
        description: 'There was an error updating your comic. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!user || !id) return;

    if (window.confirm('Are you sure you want to delete this comic? This action cannot be undone.')) {
      try {
        setSaving(true);
        await deleteContent(id, user.uid);
        
        toast({
          title: 'Comic deleted',
          description: 'Your comic has been deleted.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        navigate('/comics');
      } catch (error) {
        console.error('Error deleting comic:', error);
        toast({
          title: 'Delete failed',
          description: 'There was an error deleting your comic.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setSaving(false);
      }
    }
  };

  if (loading) {
    return (
      <Box pt={{ base: '80px', md: '90px' }} px={{ base: 3, md: 6 }} maxW="1200px" mx="auto">
        <Center py={20}>
          <Spinner size="xl" color="purple.500" />
        </Center>
      </Box>
    );
  }

  if (!comic) {
    return (
      <Box pt={{ base: '80px', md: '90px' }} px={{ base: 3, md: 6 }} maxW="1200px" mx="auto">
        <Center py={20}>
          <VStack gap={4}>
            <Text color="red.500" fontSize="lg">Comic not found</Text>
            <Button colorScheme="purple" onClick={() => navigate('/comics')}>
              Back to Comics
            </Button>
          </VStack>
        </Center>
      </Box>
    );
  }

  return (
    <Box pt={{ base: '80px', md: '90px' }} px={{ base: 3, md: 6 }} maxW="1200px" mx="auto">
      <VStack gap={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between" align="start" wrap="wrap" gap={4}>
          <HStack gap={3}>
            <IconButton
              aria-label="Back to comic"
              icon={<IconWrapper icon={FiArrowLeft} size={20} />}
              variant="ghost"
              onClick={() => navigate(`/comic/${id}`)}
            />
            <VStack align="start" gap={1}>
              <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="bold" color={textColor}>
                Edit Comic
              </Text>
              <Text fontSize="md" color={labelColor}>
                Update your comic details and volumes
              </Text>
            </VStack>
          </HStack>
          
          <HStack gap={3}>
            <Button
              leftIcon={<IconWrapper icon={FiTrash2} size={16} />}
              colorScheme="red"
              variant="outline"
              onClick={handleDelete}
              isLoading={saving}
              size={{ base: 'sm', md: 'md' }}
            >
              Delete Comic
            </Button>
            <Button
              leftIcon={<IconWrapper icon={FiEdit} size={16} />}
              colorScheme="purple"
              onClick={handleSave}
              isLoading={saving}
              size={{ base: 'sm', md: 'md' }}
            >
              Save Changes
            </Button>
          </HStack>
        </HStack>

        {/* Form */}
        <Box bg={cardBg} borderRadius="lg" p={6} border="1px solid" borderColor={borderColor}>
          <VStack gap={6} align="stretch">
            {/* Basic Information */}
            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={6}>
              {/* Title and Author */}
              <VStack align="start" gap={4}>
                <FormControl>
                  <FormLabel color={labelColor}>Comic Title *</FormLabel>
                  <Input
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    placeholder="Enter comic title"
                    bg={bgColor}
                    borderColor={borderColor}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel color={labelColor}>Author *</FormLabel>
                  <Input
                    value={formData.author}
                    onChange={(e) => handleInputChange('author', e.target.value)}
                    placeholder="Enter author name"
                    bg={bgColor}
                    borderColor={borderColor}
                  />
                </FormControl>
              </VStack>

              {/* Description */}
              <FormControl>
                <FormLabel color={labelColor}>Description *</FormLabel>
                <Textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Enter comic description"
                  rows={4}
                  bg={bgColor}
                  borderColor={borderColor}
                />
              </FormControl>
            </Grid>

            {/* Genres */}
            <FormControl>
              <FormLabel color={labelColor}>Genres (Select up to 3) *</FormLabel>
              <Flex wrap="wrap" gap={2}>
                {GENRES.map((genre) => (
                  <Button
                    key={genre}
                    size="sm"
                    variant={formData.genres.includes(genre) ? 'solid' : 'outline'}
                    colorScheme={formData.genres.includes(genre) ? 'purple' : 'gray'}
                    onClick={() => handleGenreToggle(genre)}
                    isDisabled={!formData.genres.includes(genre) && formData.genres.length >= 3}
                  >
                    {genre}
                  </Button>
                ))}
              </Flex>
            </FormControl>

            {/* Cast, Director, Writer */}
            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr 1fr' }} gap={4}>
              <FormControl>
                <FormLabel color={labelColor}>Characters</FormLabel>
                <Input
                  value={formData.cast}
                  onChange={(e) => handleInputChange('cast', e.target.value)}
                  placeholder="Enter character names"
                  bg={bgColor}
                  borderColor={borderColor}
                />
              </FormControl>

              <FormControl>
                <FormLabel color={labelColor}>Editor</FormLabel>
                <Input
                  value={formData.director}
                  onChange={(e) => handleInputChange('director', e.target.value)}
                  placeholder="Enter editor name"
                  bg={bgColor}
                  borderColor={borderColor}
                />
              </FormControl>

              <FormControl>
                <FormLabel color={labelColor}>Writer</FormLabel>
                <Input
                  value={formData.writer}
                  onChange={(e) => handleInputChange('writer', e.target.value)}
                  placeholder="Enter writer name"
                  bg={bgColor}
                  borderColor={borderColor}
                />
              </FormControl>
            </Grid>

            {/* Content Rating, Language, Tags */}
            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr 1fr' }} gap={4}>
              <FormControl>
                <FormLabel color={labelColor}>Content Rating *</FormLabel>
                <RadioGroup value={formData.contentRating} onChange={(value) => handleInputChange('contentRating', value)}>
                  <VStack align="start" gap={2}>
                    {CONTENT_RATINGS.map((rating) => (
                      <Radio key={rating.value} value={rating.value} colorScheme="purple">
                        {rating.label}
                      </Radio>
                    ))}
                  </VStack>
                </RadioGroup>
              </FormControl>

              <FormControl>
                <FormLabel color={labelColor}>Language *</FormLabel>
                <Input
                  value={formData.language}
                  onChange={(e) => handleInputChange('language', e.target.value)}
                  placeholder="Enter language"
                  bg={bgColor}
                  borderColor={borderColor}
                />
              </FormControl>

              <FormControl>
                <FormLabel color={labelColor}>Tags</FormLabel>
                <Input
                  value={formData.tags}
                  onChange={(e) => handleInputChange('tags', e.target.value)}
                  placeholder="Enter tags separated by commas"
                  bg={bgColor}
                  borderColor={borderColor}
                />
              </FormControl>
            </Grid>

            {/* Year and Duration */}
            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
              <FormControl>
                <FormLabel color={labelColor}>Year *</FormLabel>
                <Input
                  type="number"
                  value={formData.year}
                  onChange={(e) => handleInputChange('year', parseInt(e.target.value))}
                  placeholder="Enter year"
                  bg={bgColor}
                  borderColor={borderColor}
                />
              </FormControl>

              <FormControl>
                <FormLabel color={labelColor}>Total Pages</FormLabel>
                <Input
                  type="number"
                  value={formData.duration}
                  onChange={(e) => handleInputChange('duration', parseInt(e.target.value))}
                  placeholder="Enter total pages"
                  bg={bgColor}
                  borderColor={borderColor}
                />
              </FormControl>
            </Grid>

            {/* Comic Cover */}
            <FormControl>
              <FormLabel color={labelColor}>Comic Cover *</FormLabel>
              <VStack align="start" gap={4}>
                {formData.coverUrl && (
                  <Box position="relative" w="200px" h="300px" borderRadius="md" overflow="hidden">
                    <Image
                      src={formData.coverUrl}
                      alt="Comic cover"
                      w="100%"
                      h="100%"
                      objectFit="cover"
                    />
                    <IconButton
                      aria-label="Remove cover"
                      icon={<IconWrapper icon={FiX} size={16} />}
                      size="sm"
                      colorScheme="red"
                      variant="solid"
                      position="absolute"
                      top={2}
                      right={2}
                      onClick={handleCoverRemove}
                    />
                  </Box>
                )}
                <HStack>
                  <Button
                    size="sm"
                    colorScheme="purple"
                    variant="outline"
                    onClick={() => document.getElementById('cover-upload')?.click()}
                  >
                    {formData.coverUrl ? 'Change Cover' : 'Upload Cover'}
                  </Button>
                  <Input
                    id="cover-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleCoverSelect}
                    display="none"
                  />
                </HStack>
              </VStack>
            </FormControl>

            {/* Volumes Management */}
            <FormControl>
              <HStack justify="space-between" align="center">
                <FormLabel color={labelColor} mb={0}>Volumes *</FormLabel>
                <Button
                  leftIcon={<IconWrapper icon={FiPlus} size={16} />}
                  size="sm"
                  colorScheme="purple"
                  variant="outline"
                  onClick={addVolume}
                >
                  Add Volume
                </Button>
              </HStack>
              
              {formData.volumes.length > 0 && (
                <TableContainer mt={4}>
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th>Cover</Th>
                        <Th>Title</Th>
                        <Th>Pages</Th>
                        <Th>Volume File (PDF)</Th>
                        <Th>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {formData.volumes.map((volume, index) => (
                        <Tr key={volume.id}>
                          <Td>
                            <VStack align="start" gap={2}>
                              {volume.coverUrl && (
                                <Box position="relative" w="60px" h="80px" borderRadius="md" overflow="hidden">
                                  <Image
                                    src={volume.coverUrl}
                                    alt={`Volume ${index + 1} cover`}
                                    w="100%"
                                    h="100%"
                                    objectFit="cover"
                                  />
                                  <IconButton
                                    aria-label="Remove cover"
                                    icon={<IconWrapper icon={FiX} size={12} />}
                                    size="xs"
                                    colorScheme="red"
                                    variant="solid"
                                    position="absolute"
                                    top={1}
                                    right={1}
                                    onClick={() => handleVolumeCoverRemove(volume.id)}
                                  />
                                </Box>
                              )}
                              <Button
                                size="xs"
                                colorScheme="purple"
                                variant="outline"
                                onClick={() => document.getElementById(`volume-cover-${volume.id}`)?.click()}
                              >
                                {volume.coverUrl ? 'Change' : 'Upload'}
                              </Button>
                              <Input
                                id={`volume-cover-${volume.id}`}
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    handleVolumeCoverSelect(volume.id, file);
                                  }
                                }}
                                display="none"
                              />
                            </VStack>
                          </Td>
                          <Td>
                            <Input
                              value={volume.title}
                              onChange={(e) => updateVolume(volume.id, 'title', e.target.value)}
                              placeholder="Volume title"
                              size="sm"
                              bg={bgColor}
                              borderColor={borderColor}
                            />
                          </Td>
                          <Td>
                            <Input
                              type="number"
                              value={volume.pages || ''}
                              onChange={(e) => updateVolume(volume.id, 'pages', e.target.value ? parseInt(e.target.value) : undefined)}
                              placeholder="Pages (optional)"
                              size="sm"
                              bg={bgColor}
                              borderColor={borderColor}
                              w="100px"
                            />
                          </Td>
                          <Td>
                            <VStack align="start" gap={2}>
                              <HStack>
                                <Button
                                  size="xs"
                                  colorScheme="purple"
                                  variant="outline"
                                  onClick={() => document.getElementById(`volume-file-${volume.id}`)?.click()}
                                >
                                  {volume.fileName ? 'Change File' : 'Select File'}
                                </Button>
                                {volume.fileName && (
                                  <Button
                                    size="xs"
                                    colorScheme="red"
                                    variant="ghost"
                                    onClick={() => handleVolumeFileRemove(volume.id)}
                                  >
                                    Remove
                                  </Button>
                                )}
                              </HStack>
                              <Text fontSize="xs" color={labelColor} noOfLines={1} maxW="220px">
                                {volume.fileName || 'PDF required'}
                              </Text>
                              <Input
                                id={`volume-file-${volume.id}`}
                                type="file"
                                accept=".pdf,application/pdf"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    updateVolumeFile(volume.id, file);
                                  }
                                }}
                                display="none"
                              />
                            </VStack>
                          </Td>
                          <Td>
                            <Button
                              size="xs"
                              colorScheme="red"
                              variant="outline"
                              onClick={() => removeVolume(volume.id)}
                            >
                              <IconWrapper icon={FiX} size={12} />
                            </Button>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              )}
            </FormControl>
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
};

export default EditComic;
